import { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '@/hooks/useAdmin';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit, Search, Loader2, Languages, CheckSquare, Square, Eye, ArrowUp, ArrowDown, ArrowUpDown, CheckCircle2, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZES = [20, 50, 100, 200, 500, 1000, 2000];

const COLUMNS = [
  { key: 'title', label: 'Naslov', sortable: true, align: 'left' },
  { key: 'category', label: 'Kategorija', sortable: true, align: 'left' },
  { key: 'source', label: 'Izvor', sortable: false, align: 'left' },
  { key: 'status', label: 'Status', sortable: true, align: 'left' },
  { key: 'date', label: 'Originalno objavljeno', sortable: true, align: 'left' },
  { key: 'created', label: 'Uvezeno', sortable: true, align: 'left' },
  { key: 'actions', label: 'Akcije', sortable: false, align: 'right' },
];

function fmtDate(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

function fmtDateTime(val) {
  if (!val) return '—';
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
    return `${date} ${time}`;
  } catch { return '—'; }
}

function PublishAtCell({ article, authFetch, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(() => isoToLocalInput(article.publish_at));
  const [saving, setSaving] = useState(false);

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const isoVal = val ? new Date(val).toISOString() : null;
      const r = await authFetch(`${API}/admin/articles/${article.id}`, {
        method: 'PUT',
        body: JSON.stringify({ publish_at: isoVal }),
      });
      if (!r.ok) throw new Error('save failed');
      toast.success('Datum objave ažuriran');
      setEditing(false);
      onSaved?.();
    } catch {
      toast.error('Greška pri spremanju datuma');
    } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`publish-at-edit-${article.id}`}>
        <input
          type="datetime-local"
          value={val}
          onChange={e => setVal(e.target.value)}
          className="bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-[11px] text-zinc-200 outline-none"
        />
        <button onClick={save} disabled={saving} className="text-[10px] px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50" data-testid={`publish-at-save-${article.id}`}>
          {saving ? '…' : 'Spremi'}
        </button>
        <button onClick={() => { setEditing(false); setVal(isoToLocalInput(article.publish_at)); }} className="text-[10px] px-1 text-zinc-500 hover:text-zinc-200">×</button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Klikni za uređivanje datuma/vremena objave"
      className="text-left hover:text-amber-400 transition-colors"
      data-testid={`publish-at-trigger-${article.id}`}
    >
      {fmtDateTime(article.publish_at)}
    </button>
  );
}

function isoToLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    // YYYY-MM-DDTHH:mm in local time
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function hostnameOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function SortHeader({ col, sortBy, sortOrder, onClick }) {
  const active = col.key === sortBy;
  const Icon = !col.sortable ? null : active ? (sortOrder === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-4 py-3 font-medium select-none ${col.align === 'right' ? 'text-right' : ''}`}>
      {col.sortable ? (
        <button
          type="button"
          onClick={() => onClick(col.key)}
          data-testid={`sort-${col.key}`}
          className={`inline-flex items-center gap-1.5 hover:text-zinc-200 transition-colors ${active ? 'text-amber-400' : 'text-zinc-500'}`}
        >
          {col.label}
          {Icon && <Icon size={12} />}
        </button>
      ) : (
        <span className="text-zinc-500">{col.label}</span>
      )}
    </th>
  );
}

export default function ArticlesList() {
  const authFetch = useAuthFetch();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState('');
  const [qContent, setQContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadCategories = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/categories`);
      if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const loadSources = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/articles/sources`);
      if (res.ok) { const d = await res.json(); setSources(d.sources || []); }
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, sort_by: sortBy, sort_order: sortOrder });
      if (status) params.set('status', status);
      if (source) params.set('source', source);
      if (q) params.set('q', q);
      if (qContent) params.set('q_content', qContent);
      const res = await authFetch(`${API}/admin/articles?${params}`);
      if (res.ok) {
        const d = await res.json();
        setArticles(d.articles || []);
        setTotal(d.total || 0);
        setSelected(new Set());
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, page, limit, status, source, q, qContent, sortBy, sortOrder]);

  useEffect(() => { loadArticles(); }, [loadArticles]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadSources(); }, [loadSources]);

  function handleSort(key) {
    if (key === sortBy) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder(key === 'date' || key === 'created' ? 'desc' : 'asc');
    }
    setPage(1);
  }

  async function deleteArticle(id) {
    if (!window.confirm('Obrisati ovaj članak?')) return;
    await authFetch(`${API}/admin/articles/${id}`, { method: 'DELETE' });
    loadArticles();
  }

  async function togglePinned(article) {
    const next = !article.is_pinned;
    // Optimistic update so the UI feels instant
    setArticles(arr => arr.map(a => a.id === article.id ? { ...a, is_pinned: next } : a));
    try {
      const r = await authFetch(`${API}/admin/articles/${article.id}/toggle-pinned`, {
        method: 'POST', body: JSON.stringify({ is_pinned: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(next ? 'Članak istaknut na naslovnici' : 'Članak uklonjen iz istaknutih');
    } catch (e) {
      // Revert on failure
      setArticles(arr => arr.map(a => a.id === article.id ? { ...a, is_pinned: !next } : a));
      toast.error('Greška pri promjeni istaknutog statusa');
    }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Obrisati ${selected.size} odabranih članaka? Ova akcija je trajna.`)) return;
    setBulkDeleting(true);
    try {
      const r = await authFetch(`${API}/admin/articles/bulk-delete`, {
        method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }),
      });
      // Defensive: backend may return non-JSON on 502/timeout — fall back to text
      let payload = null;
      try { payload = await r.json(); } catch { payload = { detail: await r.text().catch(() => '') }; }
      if (!r.ok) throw new Error(payload?.detail || `HTTP ${r.status}`);
      toast.success(`Obrisano ${payload.deleted ?? 0} članaka`);
      loadArticles();
    } catch (err) {
      const msg = err instanceof TypeError
        ? 'Veza s poslužiteljem prekinuta — pokušajte u manjim grupama (npr. 50 odjednom).'
        : (err.message || 'Greška pri brisanju');
      toast.error(msg);
    }
    finally { setBulkDeleting(false); }
  }

  async function bulkPublish() {
    if (selected.size === 0) return;
    // Warn about already-published selections so the admin knows skips are normal
    const selectedArticles = articles.filter(a => selected.has(a.id));
    const toPublishCount = selectedArticles.filter(a => a.status !== 'published').length;
    if (toPublishCount === 0) {
      toast.info('Svi odabrani članci su već objavljeni.');
      return;
    }
    if (!window.confirm(`Objaviti ${toPublishCount} odabranih članaka?${toPublishCount < selected.size ? ` (${selected.size - toPublishCount} već objavljeno, preskačem)` : ''}`)) return;
    setBulkPublishing(true);
    try {
      const r = await authFetch(`${API}/admin/articles/bulk-publish`, {
        method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }),
      });
      let payload = null;
      try { payload = await r.json(); } catch { payload = { detail: await r.text().catch(() => '') }; }
      if (!r.ok) throw new Error(payload?.detail || `HTTP ${r.status}`);
      const pub = payload.published ?? 0;
      const skip = payload.skipped ?? 0;
      toast.success(`Objavljeno ${pub} članaka${skip ? ` · preskočeno ${skip}` : ''}`);
      loadArticles();
    } catch (err) {
      const msg = err instanceof TypeError
        ? 'Veza s poslužiteljem prekinuta — pokušajte u manjim grupama.'
        : (err.message || 'Greška pri objavljivanju');
      toast.error(msg);
    }
    finally { setBulkPublishing(false); }
  }

  function toggle(id) {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function toggleAll() {
    if (selected.size === articles.length && articles.length > 0) setSelected(new Set());
    else setSelected(new Set(articles.map(a => a.id)));
  }

  function previewHref(a) {
    if (a.status === 'published') return `/clanak/${a.slug}`;
    return `/admin/articles/${a.id}/preview`;
  }

  const getCatName = (id) => categories.find(c => c.id === id)?.name_hr || '—';
  const allSelected = articles.length > 0 && selected.size === articles.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-6 max-w-7xl" data-testid="articles-list">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Članci</h1>
        <div className="flex items-center gap-2">
          <Link to="/admin/translate-jobs" className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-md transition-colors" data-testid="mass-translate-btn">
            <Languages size={16} /> Prevedi drafts
          </Link>
          <Link to="/admin/articles/new" className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors" data-testid="new-article-btn">
            <Plus size={16} /> Nova objava
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-zinc-900 border border-zinc-800 rounded-md px-3">
          <Search size={16} className="text-zinc-500" />
          <input type="text" placeholder="Pretraži naslov / izvadak..." value={q} onChange={e => setQ(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') { setPage(1); loadArticles(); } }}
                 className="bg-transparent py-2 text-sm w-full outline-none text-zinc-200 placeholder-zinc-600" data-testid="article-search" />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-zinc-900 border border-amber-700/50 rounded-md px-3" title="Pretraži cijeli sadržaj članka (sporije, ali pronalazi riječ unutar tijela)">
          <Search size={16} className="text-amber-500" />
          <input type="text" placeholder="Pretraži unutar sadržaja članka..." value={qContent} onChange={e => setQContent(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') { setPage(1); loadArticles(); } }}
                 className="bg-transparent py-2 text-sm w-full outline-none text-zinc-200 placeholder-zinc-600" data-testid="article-content-search" />
        </div>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
                className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none" data-testid="status-filter">
          <option value="">Svi statusi</option>
          <option value="draft">Skica</option>
          <option value="published">Objavljeno</option>
          <option value="scheduled">Zakazano</option>
          <option value="archived">Arhivirano</option>
        </select>
        <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }}
                className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-300 outline-none max-w-[220px]" data-testid="source-filter">
          <option value="">Svi izvori</option>
          {sources.map(s => (
            <option key={s.value} value={s.value}>{s.label} ({s.count})</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-400">
          Po stranici:
          <select value={limit} onChange={e => { setLimit(+e.target.value); setPage(1); }}
                  data-testid="page-size-select"
                  className="bg-zinc-900 border border-zinc-800 rounded-md px-2 py-2 text-sm text-zinc-200 outline-none">
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-md bg-amber-500/10 border border-amber-500/30 px-4 py-2.5 text-sm" data-testid="bulk-bar">
          <span className="text-amber-200 font-semibold">{selected.size} odabrano</span>
          <button onClick={bulkPublish} disabled={bulkPublishing || bulkDeleting}
                  className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded text-white text-xs font-semibold disabled:opacity-50"
                  data-testid="bulk-publish-btn">
            {bulkPublishing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Objavi odabrano
          </button>
          <button onClick={bulkDelete} disabled={bulkDeleting || bulkPublishing}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-white text-xs font-semibold disabled:opacity-50"
                  data-testid="bulk-delete-btn">
            {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Obriši odabrano
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="bulk-clear">
            Odustani
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="no-articles">Nema članaka</div>
      ) : (
        <>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="articles-table">
              <thead>
                <tr className="border-b border-zinc-800 text-left">
                  <th className="px-3 py-3 w-10">
                    <button onClick={toggleAll} className="text-zinc-500 hover:text-zinc-200" data-testid="toggle-all">
                      {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                  </th>
                  {COLUMNS.map(col => (
                    <SortHeader key={col.key} col={col} sortBy={sortBy} sortOrder={sortOrder} onClick={handleSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {articles.map(a => {
                  const isSel = selected.has(a.id);
                  return (
                    <tr key={a.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${isSel ? 'bg-amber-500/5' : ''}`} data-testid={`article-row-${a.id}`}>
                      <td className="px-3 py-3">
                        <button onClick={() => toggle(a.id)} className="text-zinc-500 hover:text-amber-400" data-testid={`toggle-${a.id}`}>
                          {isSel ? <CheckSquare size={16} className="text-amber-400" /> : <Square size={16} />}
                        </button>
                      </td>
                      <td className="px-4 py-3"><Link to={`/admin/articles/${a.id}/edit`} className="text-zinc-200 hover:text-white inline-flex items-center gap-1.5">{a.is_pinned && <Pin size={11} className="text-amber-400 shrink-0" data-testid={`pinned-indicator-${a.id}`} />}<span className="line-clamp-2">{a.title_hr}</span></Link></td>
                      <td className="px-4 py-3 text-zinc-400">{getCatName(a.category_id)}</td>
                      <td className="px-4 py-3 text-zinc-500 text-xs" data-testid={`article-source-${a.id}`}>
                        {a.source_url ? (
                          <a href={a.source_article_url || a.source_url} target="_blank" rel="noopener noreferrer"
                             className="hover:text-blue-400 truncate inline-block max-w-[180px]"
                             title={a.source_article_url || a.source_url}>
                            {a.source_name || hostnameOf(a.source_url) || '—'}
                          </a>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${a.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : a.status === 'draft' ? 'bg-amber-500/20 text-amber-400' : a.status === 'scheduled' ? 'bg-purple-500/20 text-purple-400' : 'bg-zinc-700 text-zinc-400'}`}>{a.status}</span>
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs" data-testid={`article-publish-${a.id}`}>
                        <PublishAtCell article={a} authFetch={authFetch} onSaved={loadArticles} />
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs" data-testid={`article-imported-${a.id}`}>{fmtDateTime(a.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => togglePinned(a)}
                            title={a.is_pinned ? 'Ukloni iz istaknutih (naslovnica)' : 'Istakni na naslovnici (ostaje dok ne uklonite)'}
                            className={`p-1.5 ${a.is_pinned ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-500 hover:text-amber-400'}`}
                            data-testid={`toggle-pin-${a.id}`}
                          >
                            {a.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                          </button>
                          <a
                            href={previewHref(a)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={a.status === 'published' ? 'Otvori objavljeni članak' : 'Draft preview'}
                            className="p-1.5 text-zinc-500 hover:text-emerald-400"
                            data-testid={`preview-article-${a.id}`}
                          >
                            <Eye size={14} />
                          </a>
                          <Link to={`/admin/articles/${a.id}/edit`} className="p-1.5 text-zinc-500 hover:text-blue-400" data-testid={`edit-article-${a.id}`}><Edit size={14} /></Link>
                          <button onClick={() => deleteArticle(a.id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`delete-article-${a.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4 text-sm text-zinc-500">
            <span>Ukupno: {total} · Prikazano: {articles.length}</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(1)} className="px-2 py-1 bg-zinc-800 rounded disabled:opacity-30">«</button>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 bg-zinc-800 rounded disabled:opacity-30">Prethodna</button>
              <span className="px-3 py-1">Str. {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 bg-zinc-800 rounded disabled:opacity-30">Sljedeća</button>
              <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="px-2 py-1 bg-zinc-800 rounded disabled:opacity-30">»</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
